/**
 * Accès Firestore et Storage du catalogue. Le champ `loan` n'est jamais écrit
 * ici (CLAUDE.md, piège 5) : seule l'API le modifie.
 */
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type FieldValue,
  type Unsubscribe,
} from 'firebase/firestore'
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { db, storage } from '@/firebase'
import { bookConverter, loanConverter } from '@/services/converters'
import { normalize, normalizeIsbn, parseAuthors } from '@/services/format'
import type { Book, BookCandidate, BookDraft, CardExtraction, Loan } from '@/types'

const booksCol = () => collection(db, 'books').withConverter(bookConverter)

/** Écoute tout le catalogue (≤ 1 000 livres) avec le cache local persistant. */
export function subscribeBooks(onChange: (books: Book[], fromCache: boolean) => void, onError: (e: Error) => void): Unsubscribe {
  return onSnapshot(
    booksCol(),
    { includeMetadataChanges: false },
    (snap) => onChange(snap.docs.map((d) => d.data()), snap.metadata.fromCache),
    onError,
  )
}

/** Historique des prêts clôturés (RG-36), du plus récent au plus ancien. */
export async function listClosedLoans(max = 500): Promise<Loan[]> {
  // orderBy exclut les documents sans `returnedAt` : seuls les prêts clôturés.
  const q = query(collection(db, 'loans').withConverter(loanConverter), orderBy('returnedAt', 'desc'), limit(max))
  return (await getDocs(q)).docs.map((d) => d.data())
}

// --- Brouillon de formulaire --------------------------------------------------

export function emptyDraft(): BookDraft {
  return {
    title: '',
    authorsText: '',
    publisher: '',
    year: '',
    isbn: '',
    googleBooksId: '',
    description: '',
    thumbnailUrl: '',
    location: '',
    personalNotes: '',
  }
}

export function draftFromBook(b: Book): BookDraft {
  return {
    title: b.title,
    authorsText: b.authors.join(', '),
    publisher: b.publisher ?? '',
    year: b.year ? String(b.year) : '',
    isbn: b.isbn ?? '',
    googleBooksId: b.googleBooksId ?? '',
    description: b.description ?? '',
    thumbnailUrl: b.thumbnailUrl ?? '',
    location: b.location,
    personalNotes: b.personalNotes ?? '',
  }
}

export function draftFromExtraction(e: CardExtraction): BookDraft {
  return {
    ...emptyDraft(),
    title: e.title,
    authorsText: e.authors.join(', '),
    year: e.year ? String(e.year) : '',
    personalNotes: e.notes,
  }
}

/** Applique un candidat Google Books en conservant notes et emplacement (RG-44). */
export function applyCandidate(draft: BookDraft, c: BookCandidate): BookDraft {
  return {
    ...draft,
    title: c.title || draft.title,
    authorsText: c.authors.length > 0 ? c.authors.join(', ') : draft.authorsText,
    publisher: c.publisher ?? '',
    year: c.year ? String(c.year) : draft.year,
    isbn: c.isbn ?? '',
    googleBooksId: c.googleBooksId,
    description: c.description ?? '',
    thumbnailUrl: c.thumbnailUrl ?? '',
  }
}

export function parseYear(s: string): number | undefined {
  const n = Number.parseInt(s.trim(), 10)
  return Number.isInteger(n) && n > 0 && n < 3000 ? n : undefined
}

export interface DraftErrors {
  title?: string
  location?: string
  year?: string
  isbn?: string
}

export function validateDraft(d: BookDraft): DraftErrors {
  const errors: DraftErrors = {}
  if (d.title.trim() === '') errors.title = 'Le titre est obligatoire.'
  if (d.location.trim() === '') errors.location = 'L’emplacement est obligatoire.'
  if (d.year.trim() !== '' && parseYear(d.year) === undefined) errors.year = 'Année invalide.'
  if (d.isbn.trim() !== '' && normalizeIsbn(d.isbn) === '') errors.isbn = 'Cet ISBN n’est pas valide.'
  return errors
}

/** Champs descriptifs à écrire. Un champ vide est omis (création) ou supprimé (modification). */
function descriptiveFields(d: BookDraft, forUpdate: boolean): Record<string, unknown> {
  const opt = (v: string | number | undefined): string | number | FieldValue | undefined =>
    v === undefined || v === '' ? (forUpdate ? deleteField() : undefined) : v
  const fields: Record<string, unknown> = {
    title: d.title.trim(),
    authors: parseAuthors(d.authorsText),
    location: d.location.trim(),
    publisher: opt(d.publisher.trim()),
    year: opt(parseYear(d.year)),
    isbn: opt(normalizeIsbn(d.isbn)),
    googleBooksId: opt(d.googleBooksId.trim()),
    description: opt(d.description.trim()),
    thumbnailUrl: opt(d.thumbnailUrl.startsWith('https://') ? d.thumbnailUrl : ''),
    personalNotes: opt(d.personalNotes.trim()),
  }
  for (const k of Object.keys(fields)) if (fields[k] === undefined) delete fields[k]
  return fields
}

/** Crée un livre « Disponible » (RG-46, RG-47) et renvoie son identifiant. */
export async function createBook(d: BookDraft, uid: string): Promise<string> {
  const ref = doc(collection(db, 'books'))
  await setDoc(ref, {
    ...descriptiveFields(d, false),
    loan: { status: 'available' },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: uid,
  })
  return ref.id
}

/** Modifie les champs descriptifs ; jamais `loan` (RG-51). */
export async function updateBook(id: string, d: BookDraft): Promise<void> {
  await updateDoc(doc(db, 'books', id), { ...descriptiveFields(d, true), updatedAt: serverTimestamp() })
}

/**
 * Supprime un livre puis sa photo de fiche (RG-52). Les règles refusent la
 * suppression d'un livre emprunté ; l'historique des prêts est conservé.
 */
export async function deleteBook(b: Book): Promise<void> {
  await deleteDoc(doc(db, 'books', b.id))
  if (b.cardImagePath) await deleteObject(ref(storage, b.cardImagePath)).catch(() => undefined)
}

// --- Photos de fiches ---------------------------------------------------------

/** Dépose une photo compressée dans cards/tmp/<uuid>.jpg (RG-41). */
export async function uploadTmpCard(jpeg: Blob): Promise<string> {
  const path = `cards/tmp/${crypto.randomUUID()}.jpg`
  await uploadBytes(ref(storage, path), jpeg, { contentType: 'image/jpeg' })
  return path
}

const urlCache = new Map<string, Promise<string>>()

/**
 * Résout l'URL d'affichage d'une fiche sous règles Storage. L'URL n'est jamais
 * stockée (CLAUDE.md, piège 6).
 */
export function cardUrl(path: string, version?: Date): Promise<string> {
  const key = `${path}@${version?.getTime() ?? 0}`
  let p = urlCache.get(key)
  if (!p) {
    p = getDownloadURL(ref(storage, path))
    p.catch(() => urlCache.delete(key))
    urlCache.set(key, p)
  }
  return p
}

/** Retire la photo de fiche d'un livre (RG-50). */
export async function removeCard(b: Book): Promise<void> {
  if (!b.cardImagePath) return
  await updateDoc(doc(db, 'books', b.id), { cardImagePath: deleteField(), updatedAt: serverTimestamp() })
  await deleteObject(ref(storage, b.cardImagePath)).catch(() => undefined)
}

// --- Doublons (RG-45) ------------------------------------------------------------

/** Livres de même ISBN, ou de même titre et même premier auteur. */
export function findDuplicates(d: BookDraft, books: Book[], excludeId?: string): Book[] {
  const isbn = normalizeIsbn(d.isbn)
  const title = normalize(d.title)
  const firstAuthor = normalize(parseAuthors(d.authorsText)[0] ?? '')
  return books.filter((b) => {
    if (b.id === excludeId) return false
    if (isbn !== '' && b.isbn !== undefined && normalizeIsbn(b.isbn) === isbn) return true
    return title !== '' && normalize(b.title) === title && normalize(b.authors[0] ?? '') === firstAuthor
  })
}
